/**
 * One person's request: what they asked, who it is about (masked), what it
 * covers, which businesses keep them and how each is affected, what it put in
 * force, the work it caused and its whole timeline.
 *
 * The actions offered are the server's answer for this request and this
 * person (`actions`): verify, apply, reject, withdraw, retry. A request in
 * force keeps what it said; to change it, withdraw it and record a new one.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, CircleSlash, RotateCcw, ShieldCheck, Undo2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  DetailField,
  DetailGrid,
  Modal,
  PageHeader,
  QueryPending,
  Sections,
  Switch,
  Textarea,
  formatDateTime,
  humanize,
  toast,
} from '@connect/web-shared';

import { describeScope } from './scope-editor';
import {
  CHANNEL_LABEL,
  EFFECT,
  DECISION,
  EVENT,
  SOURCE,
  STATUS,
  TASK,
  requestKeys,
  requestsApi,
  type Channel,
  type RequestDetail,
  type Settings,
} from './requests-api';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : undefined);
const CRUMBS = [{ label: 'Legal' }, { label: 'Individual requests', to: '/individual-requests' }];

export function IndividualRequestPage() {
  const { publicId = '' } = useParams();
  const request = useQuery({ queryKey: requestKeys.request(publicId), queryFn: () => requestsApi.request(publicId) });
  const settings = useQuery({ queryKey: requestKeys.settings, queryFn: requestsApi.settings });
  if (request.data === undefined || settings.data === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4">
        <PageHeader title="Request" breadcrumbs={CRUMBS} />
        <QueryPending query={request.data === undefined ? request : settings} label="Loading the request" />
      </div>
    );
  }
  return <RequestView request={request.data} settings={settings.data} />;
}

type Asking = 'verify' | 'approve' | 'reject' | 'withdraw' | null;

function RequestView({ request, settings }: { request: RequestDetail; settings: Settings }) {
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState<Asking>(null);
  const status = STATUS[request.status];
  const can = (action: string) => request.actions.includes(action);
  // A business past its deadline is chased, never answered for: "retry" gives
  // it another week and a reminder (`tasks.retry` → `decisions.extend`), so it
  // is offered and explained as that rather than as work that broke.
  const unanswered = request.decisions.filter((d) => d.status === 'unanswered');
  const broken = request.tasks.find((t) => t.status === 'failed' && t.kind !== 'client_decision' && t.last_error);
  const refresh = async (data: RequestDetail) => {
    queryClient.setQueryData(requestKeys.request(request.public_id), data);
    await queryClient.invalidateQueries({ queryKey: requestKeys.list });
    await queryClient.invalidateQueries({ queryKey: requestKeys.counts });
  };
  const retry = useMutation({
    mutationFn: () => requestsApi.retry(request.public_id),
    onSuccess: async (data) => {
      toast.success(unanswered.length ? 'Another week given' : 'Trying again');
      await refresh(data);
    },
    onError: (error) => toast.error('Not retried', errorText(error)),
  });
  const share = useMutation({
    mutationFn: (value: boolean) => requestsApi.update(request.public_id, { share_reason: value }),
    onSuccess: refresh,
    onError: (error) => toast.error('Not saved', errorText(error)),
  });

  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {request.type.label}
            <Badge tone={status.tone}>{status.label}</Badge>
            {request.type.erasure ? <Badge tone="danger">Erasure</Badge> : null}
          </span>
        }
        description={`${request.code} · ${request.identifiers.map((i) => i.masked).join(', ')} · ${SOURCE[request.source]}`}
        breadcrumbs={[...CRUMBS, { label: request.code }]}
        actions={
          <div className="flex flex-wrap gap-2">
            {can('verify') ? (
              <Button variant="secondary" onClick={() => setAsking('verify')}>
                <ShieldCheck className="size-4" />
                Verify
              </Button>
            ) : null}
            {can('approve') ? (
              <Button onClick={() => setAsking('approve')}>
                <CheckCheck className="size-4" />
                Route
              </Button>
            ) : null}
            {can('retry') ? (
              <Button variant="secondary" loading={retry.isPending} onClick={() => retry.mutate()}>
                <RotateCcw className="size-4" />
                {unanswered.length ? 'Give another week' : 'Retry'}
              </Button>
            ) : null}
            {can('reject') ? (
              <Button variant="secondary" onClick={() => setAsking('reject')}>
                <CircleSlash className="size-4" />
                Reject
              </Button>
            ) : null}
            {can('withdraw') ? (
              <Button variant="danger" onClick={() => setAsking('withdraw')}>
                <Undo2 className="size-4" />
                Withdraw
              </Button>
            ) : null}
          </div>
        }
      />
      {unanswered.length ? (
        <Alert tone="warning" title="No answer by the deadline">
          {unanswered.map((d) => d.client.name).join(', ')} {unanswered.length === 1 ? 'has' : 'have'} not
          answered. Chase {unanswered.length === 1 ? 'it' : 'them'} — each business decides about its own
          customers, and nobody answers for it. Another week opens the request to{' '}
          {unanswered.length === 1 ? 'it' : 'them'} again, with a reminder.
        </Alert>
      ) : null}
      {broken ? (
        <Alert tone="danger" title="Some of its work gave up">
          {broken.last_error} Retry once the cause is fixed — what was done is not done twice.
        </Alert>
      ) : null}
      {request.status === 'pending_verification' ? (
        <Alert tone="warning" title="Not verified yet">
          Record how you checked that this is the person before it can apply.
        </Alert>
      ) : null}

      <Sections
        label="Request"
        items={[
          {
            id: 'overview',
            label: 'Overview',
            content: <Overview request={request} settings={settings} onShare={(v) => share.mutate(v)} sharing={share.isPending} />,
          },
          {
            id: 'businesses',
            label: 'Businesses',
            badge: request.holders ? <span className="text-2xs text-faint">{request.holders.length}</span> : null,
            content: <Holders request={request} />,
          },
          ...(request.decisions.length
            ? [
                {
                  id: 'answers',
                  label: 'Businesses’ answers',
                  badge: (
                    <span className="text-2xs text-faint">
                      {request.decisions.filter((d) => d.status !== 'pending' && d.status !== 'unanswered').length}/
                      {request.decisions.length}
                    </span>
                  ),
                  content: <Answers request={request} />,
                },
              ]
            : []),
          { id: 'rules', label: 'In force', content: <Rules request={request} settings={settings} /> },
          {
            id: 'work',
            label: 'Work',
            badge: request.tasks.length ? <span className="text-2xs text-faint">{request.tasks.length}</span> : null,
            content: <Work request={request} />,
          },
          { id: 'timeline', label: 'Timeline', content: <Timeline request={request} /> },
        ]}
      />

      {asking ? (
        <DecisionDialog request={request} asking={asking} onClose={() => setAsking(null)} onDone={refresh} />
      ) : null}
    </div>
  );
}

function Overview({
  request,
  settings,
  onShare,
  sharing,
}: {
  request: RequestDetail;
  settings: Settings;
  onShare: (value: boolean) => void;
  sharing: boolean;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="What they asked" description={settings.types.find((t) => t.key === request.type.key)?.description} />
        <CardBody>
          <DetailGrid>
            <DetailField label="Person">{request.identifiers.map((i) => i.masked).join(', ')}</DetailField>
            <DetailField label="Came from">
              {SOURCE[request.source]}
              {request.source_client ? ` · ${request.source_client.name}` : ''}
            </DetailField>
            <DetailField label="Covers">{describeScope(request.scope, settings.categories).join(' · ')}</DetailField>
            <DetailField label="Business they named">{request.business_hint}</DetailField>
            <DetailField label="Received">{formatDateTime(request.created_at)}</DetailField>
            <DetailField label="Applied">{request.applied_at ? formatDateTime(request.applied_at) : null}</DetailField>
            <DetailField label="Messages stopped">{request.held_at ? formatDateTime(request.held_at) : null}</DetailField>
            <DetailField label="Answer owed by">{request.respond_by ? formatDateTime(request.respond_by) : null}</DetailField>
            <DetailField label="Verified">
              {request.verified_at
                ? `${formatDateTime(request.verified_at)} · ${request.verification_note ?? humanize(request.verified_via ?? '')}`
                : null}
            </DetailField>
            <DetailField label="Decision note">{request.decision_note}</DetailField>
          </DetailGrid>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Their reason" description="In their words. Shown to a business only if you allow it." />
        <CardBody className="space-y-3">
          <p className="whitespace-pre-wrap text-sm text-body">{request.stated_reason ?? <span className="text-faint">They gave none.</span>}</p>
          {request.stated_reason && request.actions.includes('share_reason') ? (
            <Switch
              label="A business that keeps them may read this reason"
              hint="Leave it off where it names another business."
              checked={request.share_reason}
              disabled={sharing}
              onChange={(event) => onShare(event.target.checked)}
            />
          ) : null}
          {request.staff_note ? (
            <p className="text-xs text-muted">
              <span className="font-medium">Staff note:</span> {request.staff_note}
            </p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

function Holders({ request }: { request: RequestDetail }) {
  return (
    <Card>
      <CardHeader
        title="Businesses that keep them"
        description="Every client with a contact holding one of their numbers or addresses — deleted contacts too — and what this request means there."
      />
      <CardBody>
        {request.holders === null ? (
          <p className="text-sm text-muted">The request no longer keeps their details, so who holds them is not looked up.</p>
        ) : request.holders.length === 0 ? (
          <p className="text-sm text-muted">No business keeps them as a contact.</p>
        ) : (
          <ul className="divide-y divide-line text-sm">
            {request.holders.map((holder) => (
              <li key={holder.public_id} className="flex items-center gap-3 py-2">
                <span className="min-w-0 flex-1 font-medium text-heading">{holder.name}</span>
                {!holder.live ? <span className="text-xs text-faint">deleted contact</span> : null}
                <Badge tone={EFFECT[holder.effect].tone}>{EFFECT[holder.effect].label}</Badge>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Answers({ request }: { request: RequestDetail }) {
  return (
    <Card>
      <CardHeader
        title="Where it reached, and each business’s answer"
        description="Each business decides about its own customers: delete or keep (with its reason), stop or go on. Its answer is its instruction to Connect. Chase a business that has not answered; never answer for it."
      />
      <CardBody>
        <ul className="divide-y divide-line text-sm">
          {request.decisions.map((decision) => (
            <li key={decision.client.public_id} className="space-y-1 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 font-medium text-heading">{decision.client.name}</span>
                <Badge tone={DECISION[decision.status].tone}>{DECISION[decision.status].label}</Badge>
              </div>
              <p className="text-xs text-muted">
                {decision.decided_at
                  ? `Answered ${formatDateTime(decision.decided_at)}${decision.decided_by ? ` by ${decision.decided_by}` : ''}`
                  : `Answer due ${formatDateTime(decision.due_at)}`}
                {decision.reminders ? ` · reminded ${decision.reminders}×` : ''}
              </p>
              {decision.reason ? <p className="whitespace-pre-wrap text-sm text-body">{decision.reason}</p> : null}
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function Rules({ request, settings }: { request: RequestDetail; settings: Settings }) {
  const label = (key: string) => settings.categories.find((c) => c.key === key)?.label ?? key;
  return (
    <Card>
      <CardHeader
        title="What it puts in force"
        description="The most specific rule decides a message — a named business, then a business type, then everybody — then the newest. A newer request that covers an older one ends it."
      />
      <CardBody>
        {request.rules.length === 0 ? (
          <p className="text-sm text-muted">Nothing yet: it applies when it is approved.</p>
        ) : (
          <ul className="divide-y divide-line text-sm">
            {request.rules.map((rule) => (
              <li key={`${rule.effect}:${rule.level}:${rule.effective_at}`} className="flex flex-wrap items-center gap-2 py-2">
                <Badge tone={rule.effect === 'block' ? 'danger' : 'success'}>{rule.effect === 'block' ? 'Stops' : 'Lets through'}</Badge>
                <span className="text-heading">
                  {rule.categories.length ? rule.categories.map(label).join(', ') : 'Every kind'} ·{' '}
                  {rule.channels.length
                    ? rule.channels.map((c) => CHANNEL_LABEL[c as Channel] ?? c).join(', ')
                    : 'every channel'}{' '}
                  · {rule.level === 'all' ? 'every business' : rule.level === 'clients' ? 'named businesses' : 'business types'}
                </span>
                <span className="ml-auto text-xs text-muted">
                  {rule.active ? `Since ${formatDateTime(rule.effective_at)}` : `Ended · ${humanize(rule.ended_reason ?? '')}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Work({ request }: { request: RequestDetail }) {
  const tone = { pending: 'info', done: 'success', failed: 'danger', skipped: 'neutral' } as const;
  return (
    <Card>
      <CardHeader
        title="The work it caused"
        description="Done in the background and tried again with longer waits; what was done is never done twice."
      />
      <CardBody>
        {request.tasks.length === 0 ? (
          <p className="text-sm text-muted">None: its rules are all it needs.</p>
        ) : (
          <ul className="divide-y divide-line text-sm">
            {request.tasks.map((task) => (
              <li key={`${task.kind}:${task.client ?? ''}`} className="flex flex-wrap items-center gap-2 py-2">
                <span className="min-w-0 flex-1 text-heading">
                  {TASK[task.kind] ?? humanize(task.kind)}
                  {task.client ? <span className="text-muted"> · {task.client}</span> : null}
                </span>
                {task.last_error ? <span className="text-xs text-danger">{task.last_error}</span> : null}
                {task.attempts ? <span className="text-xs text-faint">{task.attempts} tries</span> : null}
                <Badge tone={tone[task.status]}>{humanize(task.status)}</Badge>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function Timeline({ request }: { request: RequestDetail }) {
  return (
    <Card>
      <CardHeader title="Timeline" description="Every step, who took it and when. Numbers and addresses are masked." />
      <CardBody>
        <ol className="space-y-2.5 text-sm">
          {request.events.map((event) => (
            <li
              key={`${event.created_at}:${event.kind}:${String(event.detail.task ?? '')}:${String(event.detail.client ?? '')}`}
              className="flex flex-wrap gap-x-3">
              <span className="w-40 shrink-0 text-xs text-muted">{formatDateTime(event.created_at)}</span>
              <span className="font-medium text-heading">{EVENT[event.kind] ?? humanize(event.kind)}</span>
              <span className="text-muted">
                {event.actor}
                {typeof event.detail.task === 'string' ? ` · ${TASK[event.detail.task] ?? event.detail.task}` : ''}
              </span>
            </li>
          ))}
        </ol>
      </CardBody>
    </Card>
  );
}

const DECISIONS: Record<Exclude<Asking, null>, { title: string; note?: string; confirm: string }> = {
  verify: { title: 'Verify this request', note: 'How you checked it is the person', confirm: 'Verify' },
  approve: {
    title: 'Route this request',
    note: undefined,
    confirm: 'Route',
  },
  reject: { title: 'Reject this request', note: 'Why it is rejected', confirm: 'Reject' },
  withdraw: { title: 'Withdraw this request', note: 'Why — the person’s words, if they gave any', confirm: 'Withdraw' },
};

function DecisionDialog({
  request,
  asking,
  onClose,
  onDone,
}: {
  request: RequestDetail;
  asking: Exclude<Asking, null>;
  onClose: () => void;
  onDone: (data: RequestDetail) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [confirmOverride, setConfirmOverride] = useState(false);
  const decision = DECISIONS[asking];
  const act = useMutation({
    mutationFn: () => {
      if (asking === 'verify') return requestsApi.verify(request.public_id, note.trim());
      if (asking === 'approve') return requestsApi.approve(request.public_id, confirmOverride);
      if (asking === 'reject') return requestsApi.reject(request.public_id, note.trim());
      return requestsApi.withdraw(request.public_id, note.trim());
    },
    onSuccess: async (data) => {
      toast.success(`${decision.confirm}: done`);
      onClose();
      await onDone(data);
    },
  });
  const consequence =
    asking === 'approve'
      ? request.type.erasure
        ? 'Every message to them stops now. Each business that keeps them is told, and their records are erased in the background.'
        : 'Every send from the next one on obeys it — those already queued or scheduled included.'
      : asking === 'withdraw'
        ? request.type.erasure
          ? 'Its stop ends now. What was already erased stays erased.'
          : 'Its stop ends now: messages reach them again, as before it.'
        : null;
  return (
    <Modal
      open
      onClose={onClose}
      title={decision.title}
      description={consequence}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={asking === 'withdraw' || asking === 'reject' ? 'danger' : 'primary'}
            loading={act.isPending}
            disabled={Boolean(decision.note) && !note.trim()}
            onClick={() => act.mutate()}
          >
            {decision.confirm}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {act.isError ? <Alert tone="danger">{errorText(act.error) ?? 'Nothing was done.'}</Alert> : null}
        {decision.note ? <Textarea label={decision.note} rows={2} value={note} onChange={(e) => setNote(e.target.value)} /> : null}
        {asking === 'approve' && request.type.effect === 'allow' ? (
          <Checkbox
            label="The person asked for this exception"
            hint="It lets through messages they may have asked to stop."
            checked={confirmOverride}
            onChange={(e) => setConfirmOverride(e.target.checked)}
          />
        ) : null}
      </div>
    </Modal>
  );
}
